import {inject, injectable} from 'inversify'
import {PaymentServiceInterface} from '@/src/ports/services'
import type {IErrorReporter} from '@/src/ports/services/monitoring'
import {IErrorReporterSymbol} from '@/src/ports/services/monitoring'
import type {IApiClientServiceFactory} from '@/src/ports/services/api'
import {IApiClientServiceFactorySymbol} from '@/src/ports/services/api'
import {OrderPayTypeData, OrderPayTypesRequest, PayType} from '@/src/entities/order'
import {PaymentError} from '@/src/core/domain/errors'
import {OperationResult, Result} from '@/src/shared/utils/result'
import {Routes} from '@/src/config/routes'

/**
 * Payment Service Implementation
 *
 * Handles payment-related API calls.
 * Returns Result type for explicit error handling.
 */
@injectable()
export class PaymentService implements PaymentServiceInterface {
  constructor(
    @inject(IErrorReporterSymbol) private errorReporter: IErrorReporter,
    @inject(IApiClientServiceFactorySymbol) private apiClientFactory: IApiClientServiceFactory,
  ) {}

  async getPayTypes(request: OrderPayTypesRequest): Promise<OperationResult<PayType[], PaymentError>> {
    try {
      const client = this.apiClientFactory()
      const payTypes = await client.post<OrderPayTypeData>(Routes.webApi.order.payTypes, {data: request})
      return Result.ok(payTypes)
    } catch (e: unknown) {
      this.errorReporter.capture(
        e instanceof Error ? e : new Error(String(e)),
        {location_id: request.location_id, delivery_service_id: request.delivery_service_id},
      )
      return Result.fail(new PaymentError('GET_PAY_TYPES_FAILED', e))
    }
  }
}
